import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { STAT_CARD_BASE, STAT_CHIP } from '../../ui/StatCard';
import { BEHAVIOUR_TAGS } from '../../../utils/behaviourTags';
import { KPI_KEYS } from '../../../utils/simulationEngine';
import { EditorHeading, Field, TextArea, TextInput } from './EditorFields';
import MetricDeltas from './MetricDeltas';

/** A team needs real options to choose between; one card is not a decision. */
export const MIN_CARDS_PER_TEAM = 2;

/** A blank option for `fn`, with a fresh id that cannot collide with the AI's. */
export const blankCard = (fn, order) => ({
  id: `${fn.key}_custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  functionKey: fn.key,
  order,
  title: '',
  description: '',
  whenChosen: '',
  leadershipAngle: '',
  behaviour: { assertiveness: 0.5, cooperativeness: 0.5, tags: [] },
  kpi: KPI_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {}),
});

/**
 * One team's options. Participants only ever see the title and description; the rest
 * is the trainer's — what the option does to the numbers, when people reach for it, and
 * the insight the room hears in the final debrief.
 */
const DeckEditor = ({ fn, cards, kpis, onChange }) => {
  const set = (i, patch) => onChange(cards.map((c, x) => (x === i ? { ...c, ...patch } : c)));
  const remove = (i) => onChange(cards.filter((_, x) => x !== i));
  const add = () => onChange([...cards, blankCard(fn, cards.length + 1)]);

  return (
    <div className={`${STAT_CARD_BASE} p-5`}>
      <EditorHeading title={`${fn.name} decision deck`}>
        The same options are offered in every round. Participants see only the title and
        description; the numbers and the insight are yours until the final debrief.
      </EditorHeading>

      <div className="space-y-3">
        {cards.map((card, i) => (
          <div key={card.id} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
            <div className="flex items-start gap-2.5">
              <span className="mt-6 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-[11px] font-semibold text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2.5">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label="Option">
                    <TextInput value={card.title} onChange={(v) => set(i, { title: v })} placeholder="What the team does, in a few words" />
                  </Field>
                  <Field label="Leadership insight" hint="Shown to the room in the final debrief.">
                    <TextInput value={card.leadershipAngle} onChange={(v) => set(i, { leadershipAngle: v })} placeholder="e.g. Classic defensive conflict." />
                  </Field>
                </div>
                <Field label="Description">
                  <TextArea value={card.description} onChange={(v) => set(i, { description: v })} rows={2} />
                </Field>
                <Field label="When people choose it">
                  <TextInput value={card.whenChosen} onChange={(v) => set(i, { whenChosen: v })} />
                </Field>
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    What it does to the numbers
                  </span>
                  <div className="mt-1">
                    <MetricDeltas kpis={kpis} value={card.kpi} onChange={(kpi) => set(i, { kpi })} />
                  </div>
                </div>
                {card.behaviour?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {card.behaviour.tags.map((tag) => BEHAVIOUR_TAGS[tag] && (
                      <span
                        key={tag}
                        className={clsx('rounded-md px-1.5 py-0.5 text-[10px] font-semibold', STAT_CHIP[BEHAVIOUR_TAGS[tag].variant] || STAT_CHIP.slate)}
                      >
                        {BEHAVIOUR_TAGS[tag].label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={cards.length <= MIN_CARDS_PER_TEAM}
                title={cards.length <= MIN_CARDS_PER_TEAM ? `A team needs at least ${MIN_CARDS_PER_TEAM} options` : 'Remove this option'}
                className="mt-6 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 disabled:opacity-30 dark:hover:bg-neutral-800"
                aria-label="Remove this option"
              >
                <Trash2 size={15} strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2.5 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 dark:border-neutral-700 dark:hover:border-neutral-600"
      >
        <Plus size={14} strokeWidth={2} className="mr-1 inline" />
        Add an option
      </button>
    </div>
  );
};

DeckEditor.propTypes = {
  fn: PropTypes.object.isRequired,
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  kpis: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DeckEditor;
